import { useLayoutEffect, useRef, useState } from 'react'
import { Table as DsTable } from '@digdir/designsystemet-react'
import { DEFAULT_LOCALE, translate, useT, type Locale } from '@/shared/i18n'
import { Checkbox } from './Checkbox'
import { Icon } from './Icon'

// ---------------------------------------------------------------------------
// Public types (the stable API — consumers depend on these)
// ---------------------------------------------------------------------------

export type Align = 'left' | 'right' | 'center'

export type Column<T> = {
  key: keyof T & string
  label: string
  align?: Align
  sortable?: boolean
  /**
   * Optional custom cell renderer. When provided, the cell shows this node
   * instead of the stringified `row[key]` — letting a column carry rich
   * content (links, badges, composed markers) while `key` still identifies
   * the column for React keys and sort. Omit for plain text cells.
   */
  render?: (row: T) => React.ReactNode
  /**
   * Minimum column width (any CSS length, e.g. '220px', '18ch'). Applied to
   * the header + cells as an inline min-width, which overrides the table's
   * `max-width: 0` ellipsis default — so an identifying column (title, name)
   * gets a floor and still truncates beyond it. Omit for auto sizing.
   */
  minWidth?: string
  /**
   * When false, the cell opts out of the ellipsis clip so its content shows in
   * full and the column sizes to fit it — use for columns whose atom must not
   * be chopped (e.g. a status badge). Defaults to true (clip + ellipsis).
   */
  clip?: boolean
}

export type SortState = {
  key: string
  direction: 'asc' | 'desc'
}

export type RowState = 'new'

type TableProps<T extends Record<string, unknown>> = {
  /** Explicit language for standalone callers outside Inertia. */
  locale?: Locale
  /** Airy two-line rows and quiet headers for overview lists. */
  variant?: 'default' | 'list'
  /** Column definitions. Order determines render order. */
  columns: Column<T>[]
  /** Data rows to render. */
  rows: T[]
  /** Extracts a stable unique string key from a row (used as React key and select id). */
  rowKey: (row: T) => string
  /** Current sort state. Undefined = no active sort. */
  sort?: SortState
  /** Called when the user clicks a sortable column header. */
  onSort?: (key: string, direction: 'asc' | 'desc') => void
  /** Enable row selection with leading checkbox column. */
  selectable?: boolean
  /** Set of currently selected row keys. Required when selectable=true. */
  selectedIds?: string[]
  /** Called with the new full set of selected ids after any selection change. */
  onSelect?: (ids: string[]) => void
  /** Called when the user clicks a data row (not the checkbox). */
  onRowClick?: (row: T) => void
  /** Return a row state ('new') to apply visual decoration to a row. */
  getRowState?: (row: T) => RowState | undefined
  /**
   * Content rendered in a single full-width row when `rows` is empty. Spans
   * every column. Omit to render an empty tbody (the default).
   */
  emptyContent?: React.ReactNode
  /**
   * Enable drag-to-resize on the column headers. The value is the key the
   * chosen widths persist under, so a resize survives the navigations the
   * table itself triggers (sort, filter, bucket switch) instead of snapping
   * back on every reload. Omit to keep the columns fixed by their definitions.
   */
  resizable?: string
}

// Narrowest a column may be dragged — below this a header label is unreadable
// and the column can no longer be grabbed back.
const MIN_COLUMN_WIDTH = 60

// How far one arrow-key press moves a resize handle.
const KEYBOARD_STEP = 16

type ColumnWidths = Record<string, number>

function storageKey(key: string): string {
  return `table-widths:${key}`
}

function readWidths(key: string | undefined): ColumnWidths {
  if (!key || typeof window === 'undefined') return {}
  try {
    const raw = window.localStorage.getItem(storageKey(key))
    const parsed: unknown = raw ? JSON.parse(raw) : null
    if (parsed == null || typeof parsed !== 'object') return {}
    // Only keep sane numbers — a hand-edited or stale entry must not be able
    // to collapse the table.
    return Object.fromEntries(
      Object.entries(parsed as Record<string, unknown>).filter(
        (entry): entry is [string, number] =>
          typeof entry[1] === 'number' && Number.isFinite(entry[1]) && entry[1] >= MIN_COLUMN_WIDTH,
      ),
    )
  } catch {
    return {}
  }
}

function writeWidths(key: string | undefined, widths: ColumnWidths): void {
  if (!key || typeof window === 'undefined') return
  try {
    window.localStorage.setItem(storageKey(key), JSON.stringify(widths))
  } catch {
    // A full or disabled localStorage costs the user their column widths, not
    // the table.
  }
}

// Bounding rectangles and pointer coordinates include shell zoom; stored widths
// and keyboard steps are CSS pixels. Use the same measurement for both paths.
function measurePair(handle: HTMLElement) {
  const header = handle.closest('th')
  const nextHeader = header?.nextElementSibling
  if (!header || !nextHeader) return
  const renderedWidth = header.getBoundingClientRect().width
  if (renderedWidth <= 0) return
  const cssWidth = parseFloat(getComputedStyle(header).width)
  const zoom = cssWidth > 0 ? renderedWidth / cssWidth :
    parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--app-zoom')) || 1
  return {
    startWidth: renderedWidth / zoom,
    nextStartWidth: nextHeader.getBoundingClientRect().width / zoom,
    zoom,
  }
}

function updateResizeValue(handle: HTMLElement) {
  const pair = measurePair(handle)
  if (!pair) return
  handle.setAttribute('aria-valuenow', String(pair.startWidth))
  handle.setAttribute('aria-valuemax', String(pair.startWidth + pair.nextStartWidth - MIN_COLUMN_WIDTH))
}

// Enter toggles selection only inside tables. Space and form Enter stay native.
function selectionKeyDown(event: React.KeyboardEvent<HTMLTableCellElement>) {
  event.stopPropagation()
  if (event.key === 'Enter' && event.target instanceof HTMLInputElement && event.target.type === 'checkbox') {
    event.preventDefault()
    event.target.click()
  }
}

// ---------------------------------------------------------------------------
// Sort button
// ---------------------------------------------------------------------------

type SortBtnProps = {
  label: string
  isActive: boolean
  direction?: 'asc' | 'desc'
  onClick: () => void
}

function SortBtn({ label, isActive, direction, onClick }: SortBtnProps) {
  function handleKeyDown(e: React.KeyboardEvent) {
    e.stopPropagation()
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      onClick()
    }
  }

  return (
    <button
      type="button"
      className={`tbl__sort${isActive ? ' tbl__sort--active' : ''}`}
      onClick={(e) => { e.stopPropagation(); onClick() }}
      onKeyDown={handleKeyDown}
      aria-label={label}
    >
      {label}
      <Icon
        name="sort"
        size={14}
        aria-hidden="true"
        style={
          isActive
            ? { transform: direction === 'desc' ? 'scaleY(-1)' : 'none' }
            : undefined
        }
      />
    </button>
  )
}

// ---------------------------------------------------------------------------
// Table component
// ---------------------------------------------------------------------------

/**
 * Presentational data table.
 *
 * Renders native Designsystemet markup with the existing `.tbl` layout hooks.
 * Props supply all content and behaviour;
 * no data fetching or privileged logic happens here.
 */
export function Table<T extends Record<string, unknown>>({ locale, ...props }: TableProps<T>) {
  return locale !== undefined || (!props.selectable && !props.resizable)
    ? <TableBody {...props} t={(key, vars) => translate(locale ?? DEFAULT_LOCALE, key, vars)} />
    : <InertiaTable {...props} />
}

function InertiaTable<T extends Record<string, unknown>>(props: TableProps<T>) {
  const t = useT()
  return <TableBody {...props} t={t} />
}

function TableBody<T extends Record<string, unknown>>({
  variant = 'default',
  columns,
  rows,
  rowKey,
  sort,
  onSort,
  selectable,
  selectedIds = [],
  onSelect,
  onRowClick,
  getRowState,
  emptyContent,
  resizable,
  t,
}: TableProps<T> & { t: ReturnType<typeof useT> }) {
  const tableRef = useRef<HTMLTableElement>(null)
  // ------------------------------------------------------------------
  // Column resizing
  // ------------------------------------------------------------------

  const [widths, setWidths] = useState<ColumnWidths>(() => readWidths(resizable))
  // Mirrors `widths` so a drag can read the live set without waiting a render,
  // and so pointerup persists what the last move actually produced.
  const widthsRef = useRef(widths)
  widthsRef.current = widths
  const dragRef = useRef<{
    key: string
    nextKey: string
    startX: number
    startWidth: number
    nextStartWidth: number
    zoom: number
  } | null>(null)

  useLayoutEffect(() => {
    const table = tableRef.current
    if (!resizable || !table) return
    function updateResizeValues() {
      table!.querySelectorAll<HTMLElement>('.tbl__resize').forEach(updateResizeValue)
    }
    updateResizeValues()
    const observer = typeof ResizeObserver === 'undefined' ? undefined : new ResizeObserver(updateResizeValues)
    observer?.observe(table)
    return () => observer?.disconnect()
  }, [resizable, columns, widths])

  /**
   * Moves the boundary between two adjacent columns: what one gains the other
   * gives up, so the table's total width — and every column outside the pair —
   * stays where the user put it. Clamped so neither side can collapse.
   */
  function resizePair(
    key: string,
    nextKey: string,
    startWidth: number,
    nextStartWidth: number,
    delta: number,
  ): ColumnWidths {
    const shift = Math.max(
      MIN_COLUMN_WIDTH - startWidth,
      Math.min(nextStartWidth - MIN_COLUMN_WIDTH, delta),
    )
    const total = startWidth + nextStartWidth
    const width = Math.max(MIN_COLUMN_WIDTH, Math.min(total - MIN_COLUMN_WIDTH, Math.round(startWidth + shift)))
    const next = {
      ...widthsRef.current,
      [key]: width,
      [nextKey]: total - width,
    }
    widthsRef.current = next
    setWidths(next)
    return next
  }

  function startResize(e: React.PointerEvent<HTMLSpanElement>, key: string, nextKey: string) {
    // Keep the press off the sort button underneath, and off any row/section
    // click handler wrapping the table.
    e.preventDefault()
    e.stopPropagation()
    const pair = measurePair(e.currentTarget)
    if (!pair) return
    dragRef.current = {
      key,
      nextKey,
      startX: e.clientX,
      ...pair,
    }
    e.currentTarget.setPointerCapture(e.pointerId)
  }

  function moveResize(e: React.PointerEvent<HTMLSpanElement>) {
    const drag = dragRef.current
    if (!drag) return
    resizePair(
      drag.key,
      drag.nextKey,
      drag.startWidth,
      drag.nextStartWidth,
      (e.clientX - drag.startX) / drag.zoom,
    )
  }

  function endResize() {
    if (!dragRef.current) return
    dragRef.current = null
    writeWidths(resizable, widthsRef.current)
  }

  // Double-click undoes the boundary the grip owns — both its columns go back
  // to automatic sizing. Resetting only one would leave the other pinned where
  // this very drag put it, and a column dragged to the minimum needs a way home.
  function resetPair(key: string, nextKey: string) {
    const next = { ...widthsRef.current }
    delete next[key]
    delete next[nextKey]
    widthsRef.current = next
    setWidths(next)
    writeWidths(resizable, next)
  }

  function nudgeWidth(e: React.KeyboardEvent<HTMLSpanElement>, key: string, nextKey: string) {
    e.stopPropagation()
    const step = e.key === 'ArrowLeft' ? -KEYBOARD_STEP : e.key === 'ArrowRight' ? KEYBOARD_STEP : 0
    if (step === 0) return
    e.preventDefault()
    const pair = measurePair(e.currentTarget)
    if (!pair) return
    writeWidths(
      resizable,
      resizePair(
        key,
        nextKey,
        pair.startWidth,
        pair.nextStartWidth,
        step,
      ),
    )
  }

  // A pinned column is pinned in all three directions — with `width` alone the
  // auto table layout still grows the column to fit its content.
  function sizeStyle(col: Column<T>): React.CSSProperties {
    const pinned = widths[col.key]
    if (pinned != null) return { width: pinned, minWidth: pinned, maxWidth: pinned }
    return col.minWidth ? { minWidth: col.minWidth } : {}
  }

  // ------------------------------------------------------------------
  // Sort handling
  // ------------------------------------------------------------------

  function handleSort(colKey: string) {
    if (!onSort) return
    if (sort?.key === colKey) {
      // Toggle direction
      onSort(colKey, sort.direction === 'asc' ? 'desc' : 'asc')
    } else {
      onSort(colKey, 'asc')
    }
  }

  // ------------------------------------------------------------------
  // Selection handling
  // ------------------------------------------------------------------

  const allKeys = rows.map(rowKey)
  const selectedSet = new Set(selectedIds)
  const allSelected = allKeys.length > 0 && allKeys.every((k) => selectedSet.has(k))
  const someSelected = !allSelected && allKeys.some((k) => selectedSet.has(k))

  function handleSelectAll() {
    if (!onSelect) return
    if (allSelected) {
      onSelect([])
    } else {
      onSelect(allKeys)
    }
  }

  function handleSelectRow(key: string) {
    if (!onSelect) return
    if (selectedSet.has(key)) {
      onSelect(selectedIds.filter((id) => id !== key))
    } else {
      onSelect([...selectedIds, key])
    }
  }

  // ------------------------------------------------------------------
  // Render
  // ------------------------------------------------------------------

  return (
    <div className="tbl-scroll" role="region" tabIndex={0} aria-label={columns.map(column => column.label).filter(Boolean).join(', ') || t('chrome.table.scroll_hint')}>
      <p className="tbl-scroll__hint">{t('chrome.table.scroll_hint')}</p>
      <DsTable ref={tableRef} hover stickyHeader className={variant === 'list' ? 'tbl tbl--list' : 'tbl'}>
        <DsTable.Head>
          <DsTable.Row>
            {selectable && (
              <DsTable.HeaderCell scope="col" className="tbl__selection" style={{ width: 40 }}
                onClick={(e) => e.stopPropagation()} onKeyDown={selectionKeyDown}>
                <Checkbox
                  checked={allSelected}
                  indeterminate={someSelected}
                  onChange={handleSelectAll}
                  label={t('chrome.table.select_all')}
                  hideLabel
                />
              </DsTable.HeaderCell>
            )}
            {columns.map((col, index) => {
              const isSorted = sort?.key === col.key
              // A grip sits on a boundary between two columns; the trailing
              // edge of the last one has nothing to trade with.
              const nextKey = columns[index + 1]?.key
              const ariaSort: 'ascending' | 'descending' | 'none' | undefined =
                col.sortable
                  ? isSorted
                    ? sort!.direction === 'asc'
                      ? 'ascending'
                      : 'descending'
                    : 'none'
                  : undefined
              return (
              <DsTable.HeaderCell
                key={col.key}
                scope="col"
                aria-sort={ariaSort}
                style={{
                  ...(col.align === 'right' ? { textAlign: 'right' } : {}),
                  ...sizeStyle(col),
                }}
              >
                {col.sortable ? (
                  <SortBtn
                    label={col.label}
                    isActive={sort?.key === col.key}
                    direction={sort?.key === col.key ? sort.direction : undefined}
                    onClick={() => handleSort(col.key)}
                  />
                ) : (
                  col.label
                )}
                {resizable && nextKey && (
                  <span
                    className="tbl__resize"
                    role="separator"
                    aria-orientation="vertical"
                    aria-label={t('chrome.table.resize_column', { column: col.label || col.key })}
                    aria-valuemin={MIN_COLUMN_WIDTH}
                    aria-valuenow={widths[col.key] ?? MIN_COLUMN_WIDTH}
                    tabIndex={0}
                    onPointerDown={(e) => startResize(e, col.key, nextKey)}
                    onPointerMove={moveResize}
                    onPointerUp={endResize}
                    onPointerCancel={endResize}
                    onClick={(e) => e.stopPropagation()}
                    onDoubleClick={(e) => { e.stopPropagation(); resetPair(col.key, nextKey) }}
                    onFocus={(e) => updateResizeValue(e.currentTarget)}
                    onKeyDown={(e) => nudgeWidth(e, col.key, nextKey)}
                  />
                )}
              </DsTable.HeaderCell>
              )
            })}
          </DsTable.Row>
        </DsTable.Head>
        <DsTable.Body>
          {rows.length === 0 && emptyContent != null && (
            <DsTable.Row>
              <DsTable.Cell colSpan={columns.length + (selectable ? 1 : 0)} className="tbl__empty">
                {emptyContent}
              </DsTable.Cell>
            </DsTable.Row>
          )}
          {rows.map((row) => {
            const key = rowKey(row)
            const isSelected = selectedSet.has(key)
            const rowState = getRowState?.(row)

            const trClassName = [
              isSelected ? 'is-selected' : '',
              rowState === 'new' ? 'row-new' : '',
            ]
              .filter(Boolean)
              .join(' ') || undefined

            return (
              <DsTable.Row
                key={key}
                className={trClassName}
                style={onRowClick ? { cursor: 'pointer' } : undefined}
                // A clickable row stays a table row — role="button" on a <tr> is
                // invalid ARIA and strips it from the table's row semantics.
                // It is made keyboard-operable instead: a tab stop that activates
                // on Enter / Space (WCAG 2.1.1), with a focus-visible outline from
                // components.css. The keydown only acts when the row itself holds
                // focus — interactive cells (the selection checkbox) stop
                // propagation on their own.
                tabIndex={onRowClick ? 0 : undefined}
                onClick={onRowClick ? (e) => {
                  if ((e.target as Element).closest('a, button, input, select, textarea, label, [role="button"], [role="checkbox"], [role="separator"]')) return
                  onRowClick(row)
                } : undefined}
                onKeyDown={
                  onRowClick
                    ? (e) => {
                        if (e.target !== e.currentTarget) return
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault()
                          onRowClick(row)
                        }
                      }
                    : undefined
                }
              >
                {selectable && (
                  <DsTable.Cell
                    className="tbl__selection"
                    style={{ width: 40 }}
                    onClick={(e) => e.stopPropagation()}
                    onKeyDown={selectionKeyDown}
                  >
                    <Checkbox
                      checked={isSelected}
                      onChange={() => handleSelectRow(key)}
                      label={t('chrome.table.select_row', { id: key })}
                      hideLabel
                    />
                  </DsTable.Cell>
                )}
                {columns.map((col) => {
                  const value = row[col.key]
                  const cellClass = [
                    col.align === 'right' ? 'td--num' : '',
                    col.clip === false ? 'tbl__cell--noclip' : '',
                  ]
                    .filter(Boolean)
                    .join(' ') || undefined
                  return (
                    <DsTable.Cell key={col.key} className={cellClass} style={sizeStyle(col)}>
                      {col.render
                        ? col.render(row)
                        : value == null
                          ? ''
                          : String(value)}
                    </DsTable.Cell>
                  )
                })}
              </DsTable.Row>
            )
          })}
        </DsTable.Body>
      </DsTable>
    </div>
  )
}
